import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Ship,
  Coffee,
  Leaf,
  Tractor,
  Package,
  Sprout,
  Truck
} from "lucide-react";
import { tradingContent } from "../data/tradeContent";
import { LOGO_URL } from "../data/content";

function TradeNav({ t, lang, setLang }) {
  const navigate = useNavigate();
  return (
      <nav className="nav-fixed nav-scrolled" data-testid="trade-navigation">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="trade-logo-link">
              <img src={LOGO_URL} alt="Skymoon" className="h-12 w-auto" />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#C5A059] transition-colors"
                data-testid="trade-back-home"
              >
                <ArrowLeft size={16} />
                {t.back}
              </button>
              <div className="lang-toggle">
                <button
                  onClick={() => setLang("fr")}
                  className={`lang-btn ${lang === "fr" ? "active" : ""}`}
                  data-testid="trade-lang-fr"
                >
                  FR
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`lang-btn ${lang === "en" ? "active" : ""}`}
                  data-testid="trade-lang-en"
                >
                  EN
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
  );
}

function TradeHero({ t }) {
  return (
      <section className="pt-32 pb-16 px-6 md:px-12" data-testid="trade-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
              SKYMOON CONSULTANCY
            </p>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-[#F8FAFC] mb-4">
              {t.hero.title}
            </h1>
            <p className="text-xl text-[#C5A059] mb-4">{t.hero.subtitle}</p>
            <p className="text-lg text-[#94A3B8] max-w-2xl">
              {t.hero.description}
            </p>
          </motion.div>
        </div>
      </section>
  );
}

function LinguitiSection({ t }) {
  return (
      <section className="section-padding bg-gradient-to-br from-[#0F1420] via-[#1B2A4E] to-[#0F1420] relative overflow-hidden" data-testid="linguiti-brand">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
                  {t.brands.label}
                </p>
                <h2 className="text-4xl md:text-6xl font-serif font-bold text-[#F8FAFC] mb-3">
                  <span className="text-red-500">LINGUITI</span> Tomato
                </h2>
                <p className="text-xl text-[#C5A059] mb-6">{t.brands.subtitle}</p>
                <p className="text-[#94A3B8] leading-relaxed mb-8">
                  {t.brands.desc}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {t.brands.details.map((detail, i) => (
                    <div key={detail.label} className="bg-[#161B28]/60 backdrop-blur-sm border border-white/5 rounded-lg p-4">
                      <p className="text-xs font-mono text-[#94A3B8] mb-1">{detail.label}</p>
                      <p className="text-[#F8FAFC] font-semibold">{detail.value}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="rounded-lg overflow-hidden shadow-2xl aspect-[4/5]">
                  <img
                    src="https://customer-assets-jt897jd0.emergentagent.net/job_skymoon-invest/artifacts/jbdrve6d_IMG_4448.jpeg"
                    alt="Linguiti Tomato - Container Export"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-red-600 text-white px-6 py-3 rounded-lg shadow-xl">
                  <p className="text-xs font-mono opacity-80">MARQUE PROPRE</p>
                  <p className="font-serif text-2xl font-bold italic">Linguiti</p>
                </div>
              </div>
            </div>

            {/* Additional Images */}
            <div className="mt-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative rounded-lg overflow-hidden group bg-white max-w-3xl mx-auto"
                data-testid="linguiti-container-img"
              >
                <div className="h-96 flex items-center justify-center overflow-hidden">
                  <img
                    src="https://customer-assets-jt897jd0.emergentagent.net/job_skymoon-invest/artifacts/u1mynjwh_IMG_4076.webp"
                    alt="Linguiti Tomato Label"
                    className="w-full h-auto object-contain scale-[1.8] translate-y-4"
                    style={{ objectPosition: 'center 65%' }}
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#05080F] to-transparent">
                  <p className="text-[#C5A059] text-sm font-mono">PACKAGING</p>
                  <p className="text-[#F8FAFC]">Étiquette Linguiti Tomato — 800g</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function TradePartnersSection({ t }) {
  return (
      <section className="section-padding" data-testid="trade-partners">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
              {t.partners.label}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#F8FAFC] mb-4 section-title">
              {t.partners.title}
            </h2>
            <p className="text-[#94A3B8] mb-12 max-w-2xl">
              {t.partners.subtitle}
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {t.partners.items.map((partner, i) => (
                <motion.div
                  key={partner.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="bg-gradient-to-br from-[#161B28] to-[#0F1420] border border-white/5 rounded-lg p-8 hover:border-[#C5A059]/30 transition-all"
                  data-testid={`trade-partner-${i + 1}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 rounded-lg">
                        {i === 0 && <Coffee className="text-[#C5A059]" size={24} />}
                        {i === 1 && <Package className="text-[#C5A059]" size={24} />}
                      </div>
                      <div>
                        <p className="text-xs font-mono text-[#94A3B8] tracking-wider">
                          {partner.category}
                        </p>
                        <h3 className="text-2xl font-serif font-bold text-[#F8FAFC]">
                          {partner.name}
                        </h3>
                      </div>
                    </div>
                    <span className="text-xs font-mono px-3 py-1 bg-[#C5A059]/10 text-[#C5A059] rounded-full">
                      {partner.flag} · {partner.country}
                    </span>
                  </div>
                  <p className="text-[#94A3B8] leading-relaxed">
                    {partner.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function ActivitiesSection({ t }) {
  return (
      <section className="section-padding bg-[#0F1420]" data-testid="trade-activities">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
              {t.activities.label}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#F8FAFC] mb-4 section-title">
              {t.activities.title}
            </h2>
            <p className="text-[#94A3B8] mb-12 max-w-2xl">
              {t.activities.subtitle}
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {t.activities.items.map((item, i) => {
                const isNegotiation = item.status === "En négociation" || item.status === "Negotiation";
                return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#161B28] border border-white/5 rounded-lg p-6 hover:border-[#C5A059]/30 transition-all"
                  data-testid={`trade-item-${i + 1}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C5A059]/10 rounded-lg">
                      {i === 0 && <Coffee className="text-[#C5A059]" size={20} />}
                      {i === 1 && <Package className="text-[#C5A059]" size={20} />}
                      {i === 2 && <Ship className="text-[#C5A059]" size={20} />}
                      {i === 3 && <Truck className="text-[#C5A059]" size={20} />}
                      {i === 4 && <Package className="text-[#C5A059]" size={20} />}
                    </div>
                    <span className={`text-xs font-mono px-2 py-1 rounded ${
                      isNegotiation 
                        ? "bg-amber-500/10 text-amber-400" 
                        : "bg-green-500/10 text-green-400"
                    }`}>
                      {item.status}
                    </span>
                  </div>
                  <h3 className="text-xl font-serif font-semibold text-[#F8FAFC] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#C5A059] text-sm font-mono mb-3">{item.route}</p>
                  <p className="text-[#94A3B8] text-sm">{item.desc}</p>
                </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function ProjectsSection({ t }) {
  return (
      <section className="section-padding" data-testid="trade-projects">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-[#C5A059] font-mono text-sm tracking-widest mb-4">
              {t.projects.label}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#F8FAFC] mb-4 section-title">
              {t.projects.title}
            </h2>
            <p className="text-[#94A3B8] mb-12 max-w-2xl">
              {t.projects.subtitle}
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.projects.items.map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#0F1420] border border-white/5 rounded-lg p-6 relative overflow-hidden"
                  data-testid={`project-item-${i + 1}`}
                >
                  <div className="absolute top-0 right-0 w-16 h-16 bg-[#C5A059]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-4">
                      {i === 0 && <Leaf className="text-[#C5A059]" size={18} />}
                      {i === 1 && <Tractor className="text-[#C5A059]" size={18} />}
                      {i === 2 && <Sprout className="text-[#C5A059]" size={18} />}
                      {i === 3 && <Package className="text-[#C5A059]" size={18} />}
                      <span className="text-xs font-mono px-2 py-1 bg-amber-500/10 text-amber-400 rounded">
                        {item.status}
                      </span>
                    </div>
                    <h3 className="text-lg font-serif font-semibold text-[#F8FAFC] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-[#94A3B8] text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
  );
}

function TradeFooter() {
  return (
      <footer className="footer py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Skymoon" className="h-10 w-auto" />
            </Link>
            <p className="text-sm text-[#64748B]">
              © {new Date().getFullYear()} Skymoon Consultancy
            </p>
          </div>
        </div>
      </footer>
  );
}

export default function TradePage() {
  const [lang, setLang] = useState("fr");
  const t = tradingContent[lang];

  useEffect(() => {
    document.title = lang === "fr"
      ? "Commerce International | Skymoon Consultancy"
      : "International Trade | Skymoon Consultancy";
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#05080F]">
      <div className="noise-overlay" />
      <TradeNav t={t} lang={lang} setLang={setLang} />
      <TradeHero t={t} />
      <LinguitiSection t={t} />
      <TradePartnersSection t={t} />
      <ActivitiesSection t={t} />
      <ProjectsSection t={t} />
      <TradeFooter />
    </div>
  );
}
